import React, { useState, useEffect } from 'react';
import { teamsAPI } from '../api/client';
import {
  Bot, Calendar, Clock, CheckCircle, XCircle, Play,
  Trash2, Save, RefreshCw, Plus, X
} from 'lucide-react';

const DAYS = [
  { value: 1, label: 'Mon' },
  { value: 2, label: 'Tue' },
  { value: 3, label: 'Wed' },
  { value: 4, label: 'Thu' },
  { value: 5, label: 'Fri' },
  { value: 6, label: 'Sat' },
  { value: 7, label: 'Sun' },
];

function fmt(d) {
  if (!d) return '—';
  return new Date(d).toLocaleString('en-GB', { day: 'numeric', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit' });
}

function fmtDur(secs) {
  if (!secs) return '—';
  const m = Math.floor(secs / 60);
  const s = secs % 60;
  return m > 0 ? `${m}m ${s}s` : `${s}s`;
}

// SQL Server TIME columns are serialised as "1970-01-01T09:00:00.000Z" — extract HH:MM
function parseTimeUTC(v) {
  if (!v) return '';
  const s = String(v);
  return s.includes('T') ? s.slice(11, 16) : s.slice(0, 5);
}

export default function AIStandup() {
  const [schedule, setSchedule]   = useState(null);
  const [sessions, setSessions]   = useState([]);
  const [loading, setLoading]     = useState(true);
  const [saving, setSaving]       = useState(false);
  const [deleting, setDeleting]   = useState(false);
  const [msg, setMsg]             = useState({ type: '', text: '' });
  const [showForm, setShowForm]   = useState(false);

  // Form state
  const [days, setDays]           = useState([1,2,3,4,5]);
  const [timeUTC, setTimeUTC]     = useState('09:00');
  const [duration, setDuration]   = useState(30);
  const [subject, setSubject]     = useState('Finance Standup');

  useEffect(() => {
    Promise.all([teamsAPI.getSchedule(), teamsAPI.getSessions()])
      .then(([sc, sess]) => {
        const s = sc.data;
        setSchedule(s);
        setSessions(sess.data || []);
        if (s) {
          setDays(JSON.parse(s.DaysOfWeek || '[1,2,3,4,5]'));
          setTimeUTC(parseTimeUTC(s.TimeUTC) || '09:00');
          setDuration(s.DurationMinutes || 30);
          setSubject(s.MeetingSubject || 'Finance Standup');
        }
      })
      .catch(() => {})
      .finally(() => setLoading(false));
  }, []);

  const save = async () => {
    if (!days.length) return setMsg({ type: 'error', text: 'Select at least one day' });
    setSaving(true);
    setMsg({ type: '', text: '' });
    try {
      await teamsAPI.saveSchedule({ daysOfWeek: days, timeUTC, durationMinutes: duration, meetingSubject: subject });
      const r = await teamsAPI.getSchedule();
      setSchedule(r.data);
      setShowForm(false);
      setMsg({ type: 'success', text: 'Schedule saved' });
    } catch {
      setMsg({ type: 'error', text: 'Failed to save schedule' });
    } finally {
      setSaving(false);
    }
  };

  const deleteSchedule = async () => {
    if (!window.confirm('Delete your standup schedule?')) return;
    setDeleting(true);
    try {
      await teamsAPI.deleteSchedule();
      setSchedule(null);
      setMsg({ type: 'success', text: 'Schedule deleted' });
    } catch {
      setMsg({ type: 'error', text: 'Failed to delete' });
    } finally {
      setDeleting(false);
    }
  };

  const toggleDay = (d) => setDays(prev => prev.includes(d) ? prev.filter(x => x !== d) : [...prev, d].sort((a,b)=>a-b));

  const dayLabels = (arr) => {
    if (!arr || !arr.length) return '—';
    return arr.map(d => DAYS.find(x => x.value === d)?.label).filter(Boolean).join(', ');
  };

  return (
    <div className="max-w-3xl mx-auto px-4 py-6 space-y-6">
      {/* Header */}
      <div className="flex items-center gap-3">
        <div className="p-2.5 bg-purple-100 rounded-xl">
          <Bot className="w-6 h-6 text-purple-600" />
        </div>
        <div>
          <h1 className="text-xl font-bold text-gray-900">AI Standup</h1>
          <p className="text-sm text-gray-500">Schedule automated finance standups in Microsoft Teams</p>
        </div>
      </div>

      {/* Message */}
      {msg.text && (
        <div className={`flex items-center gap-2 px-4 py-2.5 rounded-lg text-sm ${
          msg.type === 'success' ? 'bg-green-50 text-green-700' : 'bg-red-50 text-red-700'
        }`}>
          {msg.type === 'success' ? <CheckCircle className="w-4 h-4" /> : <XCircle className="w-4 h-4" />}
          {msg.text}
        </div>
      )}

      {loading ? (
        <div className="flex justify-center py-12"><RefreshCw className="w-6 h-6 text-gray-400 animate-spin" /></div>
      ) : (
        <>
          {/* Current schedule */}
          <div className="bg-white border border-gray-200 rounded-lg overflow-hidden">
            <div className="px-4 py-3 border-b border-gray-100 flex items-center justify-between">
              <div className="flex items-center gap-2">
                <Calendar className="w-4 h-4 text-gray-400" />
                <span className="text-sm font-medium text-gray-700">Standup Schedule</span>
              </div>
              {schedule && !showForm && (
                <div className="flex gap-2">
                  <button onClick={() => setShowForm(true)}
                    className="text-xs text-blue-600 hover:text-blue-700 font-medium">Edit</button>
                  <button onClick={deleteSchedule} disabled={deleting}
                    className="text-xs text-red-500 hover:text-red-600 font-medium flex items-center gap-1">
                    <Trash2 className="w-3 h-3" /> Delete
                  </button>
                </div>
              )}
            </div>
            <div className="px-4 py-4">
              {!schedule && !showForm ? (
                <div className="text-center py-4">
                  <p className="text-sm text-gray-500 mb-3">No standup schedule set up yet</p>
                  <button onClick={() => setShowForm(true)}
                    className="inline-flex items-center gap-1.5 px-4 py-2 bg-purple-600 text-white rounded-lg text-sm font-medium hover:bg-purple-700">
                    <Plus className="w-4 h-4" /> Set Up Standup
                  </button>
                </div>
              ) : !showForm ? (
                <div className="grid grid-cols-2 gap-3 text-sm">
                  <div>
                    <p className="text-xs text-gray-400 uppercase tracking-wide mb-1">Days</p>
                    <p className="font-medium text-gray-800">{dayLabels(JSON.parse(schedule.DaysOfWeek || '[]'))}</p>
                  </div>
                  <div>
                    <p className="text-xs text-gray-400 uppercase tracking-wide mb-1">Time (UTC)</p>
                    <p className="font-medium text-gray-800">{parseTimeUTC(schedule.TimeUTC) || '—'}</p>
                  </div>
                  <div>
                    <p className="text-xs text-gray-400 uppercase tracking-wide mb-1">Duration</p>
                    <p className="font-medium text-gray-800">{schedule.DurationMinutes} minutes</p>
                  </div>
                  <div>
                    <p className="text-xs text-gray-400 uppercase tracking-wide mb-1">Subject</p>
                    <p className="font-medium text-gray-800">{schedule.MeetingSubject || '—'}</p>
                  </div>
                  {schedule.TeamsMeetingUrl && (
                    <div className="col-span-2">
                      <p className="text-xs text-gray-400 uppercase tracking-wide mb-1">Teams Meeting</p>
                      <a href={schedule.TeamsMeetingUrl} target="_blank" rel="noopener noreferrer"
                        className="text-blue-600 hover:underline text-xs">Join Link</a>
                    </div>
                  )}
                </div>
              ) : (
                /* Edit form */
                <div className="space-y-4">
                  {/* Days */}
                  <div>
                    <label className="text-xs font-medium text-gray-500 uppercase tracking-wide block mb-2">Days of week</label>
                    <div className="flex gap-2 flex-wrap">
                      {DAYS.map(d => (
                        <button key={d.value} type="button" onClick={() => toggleDay(d.value)}
                          className={`px-3 py-1.5 rounded-lg text-sm font-medium transition-colors ${
                            days.includes(d.value)
                              ? 'bg-purple-600 text-white'
                              : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                          }`}
                        >{d.label}</button>
                      ))}
                    </div>
                  </div>
                  {/* Time + Duration */}
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <label className="text-xs font-medium text-gray-500 uppercase tracking-wide block mb-1">Time (UTC)</label>
                      <input type="time" value={timeUTC} onChange={e => setTimeUTC(e.target.value)}
                        className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-purple-500" />
                    </div>
                    <div>
                      <label className="text-xs font-medium text-gray-500 uppercase tracking-wide block mb-1">Duration (min)</label>
                      <select value={duration} onChange={e => setDuration(Number(e.target.value))}
                        className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-purple-500">
                        {[15,20,30,45,60].map(m => <option key={m} value={m}>{m} min</option>)}
                      </select>
                    </div>
                  </div>
                  {/* Subject */}
                  <div>
                    <label className="text-xs font-medium text-gray-500 uppercase tracking-wide block mb-1">Meeting Subject</label>
                    <input type="text" value={subject} onChange={e => setSubject(e.target.value)} maxLength={200}
                      className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-purple-500" />
                  </div>
                  {/* Buttons */}
                  <div className="flex gap-2 pt-1">
                    <button onClick={save} disabled={saving}
                      className="inline-flex items-center gap-1.5 px-4 py-2 bg-purple-600 text-white rounded-lg text-sm font-medium hover:bg-purple-700 disabled:opacity-50">
                      {saving ? <RefreshCw className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />}
                      {saving ? 'Saving…' : 'Save Schedule'}
                    </button>
                    <button onClick={() => setShowForm(false)}
                      className="px-4 py-2 text-sm text-gray-600 hover:text-gray-800 font-medium">Cancel</button>
                  </div>
                </div>
              )}
            </div>
          </div>

          {/* Session history */}
          <div className="bg-white border border-gray-200 rounded-lg overflow-hidden">
            <div className="px-4 py-3 border-b border-gray-100 flex items-center gap-2">
              <Clock className="w-4 h-4 text-gray-400" />
              <span className="text-sm font-medium text-gray-700">Session History</span>
            </div>
            {!sessions.length ? (
              <p className="px-4 py-6 text-sm text-center text-gray-400">No standup sessions yet</p>
            ) : (
              <div className="divide-y divide-gray-50">
                {sessions.map(s => (
                  <div key={s.SessionID} className="px-4 py-3 flex items-center justify-between text-sm">
                    <div>
                      <p className="font-medium text-gray-800">{fmt(s.CompletedAt || s.StartedAt || s.CreatedAt)}</p>
                      <p className="text-xs text-gray-400 mt-0.5">
                        {s.InvoicesReviewed} reviewed · {s.InvoicesApproved} approved · {s.InvoicesRejected} rejected
                        {s.InvoicesHeld > 0 ? ` · ${s.InvoicesHeld} on hold` : ''}
                        {s.DurationSeconds ? ` · ${fmtDur(s.DurationSeconds)}` : ''}
                      </p>
                    </div>
                    <div className="flex items-center gap-2">
                      <span className={`text-xs px-2 py-0.5 rounded-full font-medium ${
                        s.CompletedAt ? 'bg-green-100 text-green-700' : 'bg-gray-100 text-gray-500'
                      }`}>
                        {s.CompletedAt ? 'Completed' : 'In progress'}
                      </span>
                      {s.TeamsMeetingUrl && (
                        <a href={s.TeamsMeetingUrl} target="_blank" rel="noopener noreferrer"
                          className="text-xs text-blue-500 hover:underline">Recording</a>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </>
      )}
    </div>
  );
}
